import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Truck, Store, ShieldCheck, Tag, Flame, Package } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { STORE_ADDRESS } from "@/constants/store";
import FaqSection from "@/components/home/FaqSection";
import ProductCard from "@/components/shop/ProductCard";
import MemberBanner from "@/components/home/MemberBanner";
import HalloweenBanner from "@/components/home/HalloweenBanner";
import EbikeSpotlight from "@/components/home/EbikeSpotlight";
import VideoHero from "@/components/home/VideoHero";
import StatsCounter from "@/components/home/StatsCounter";
import TestimonialSlider from "@/components/home/TestimonialSlider";
import Marquee from "@/components/home/Marquee";
import DealSpotlight from "@/components/home/DealSpotlight";
import Reveal from "@/components/Reveal";
import KineticText from "@/components/KineticText";

const CATEGORY_TILES = [
  {
    name: "Recliners",
    img: "https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=600",
  },
  {
    name: "Furniture",
    img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600",
  },
  {
    name: "Health & Beauty",
    img: "https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=600",
  },
  {
    name: "E-Bikes",
    img: "https://images.unsplash.com/photo-1624243519828-52a0f2c88af3?w=600",
  },
  {
    name: "Appliances",
    img: "https://images.unsplash.com/photo-1484154218962-a197022b5858?w=600",
  },
  {
    name: "Closeout & Overstock Pallets",
    img: "https://images.pexels.com/photos/36504458/pexels-photo-36504458.jpeg?w=600",
    accent: true,
  },
];

const FULFILLMENT_CARDS = [
  {
    icon: Store,
    title: "Free store pickup",
    desc: `Ready during store hours at ${STORE_ADDRESS.short}.`,
    link: "/shop?avail=today",
    cta: "Shop pickup today",
    testid: "fulfillment-card-pickup",
  },
  {
    icon: Truck,
    title: "Local delivery",
    desc: "FREE on orders $299+ within 10 miles. Auto-quoted at checkout.",
    link: "/policies/delivery",
    cta: "See delivery fees",
    testid: "fulfillment-card-delivery",
  },
  {
    icon: Package,
    title: "Special order",
    desc: "Don't see it on the floor? We bring it in for you in about a week.",
    link: "/shop?avail=order",
    cta: "Shop special order",
    testid: "fulfillment-card-special",
  },
];

export default function HomePage() {
  usePageMeta(
    null,
    "Brand-new closeout and overstock furniture, mattresses, appliances, e-bikes and liquidation pallets in Grand Rapids, Michigan. Free delivery on orders $299+ within 10 miles. Serving Grand Rapids and West Michigan."
  );
  const [featured, setFeatured] = useState([]);
  const [deals, setDeals] = useState([]);
  const [under20, setUnder20] = useState([]);
  const [liveCats, setLiveCats] = useState(null);

  useEffect(() => {
    api.get("/products/featured").then((r) => setFeatured(r.data.items));
    api.get("/products/deals").then((r) => setDeals(r.data)).catch(() => {});
    api.get("/products/categories").then((r) => setLiveCats(r.data.categories || [])).catch(() => {});
    api
      .get("/products", { params: { max_price: 20, limit: 12, sort: "price_asc" } })
      .then((r) => setUnder20(r.data.items || []))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Member savings ribbon — Walmart+/Sam's Club style */}
      <MemberBanner />

      {/* CINEMATIC VIDEO HERO */}
      <VideoHero />

      {/* COUNT-UP STATS */}
      <StatsCounter />

      {/* CONTINUOUS MARQUEE TICKER */}
      <Marquee />

      {/* AUTO-CYCLING DEAL SPOTLIGHT */}
      <DealSpotlight />

      {/* TRUST STRIP */}
      <section className="bg-gray-100 py-6 border-y border-gray-200" data-testid="trust-strip">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 flex flex-wrap justify-around text-center gap-x-4 gap-y-5 text-sm font-semibold text-[#0A1C40]">
          {[
            { icon: Truck, label: "FREE delivery $299+ within 10 mi" },
            { icon: Store, label: "Pickup ready during store hours" },
            { icon: ShieldCheck, label: "Secure Square checkout" },
            { icon: Tag, label: "Wholesale pallet pricing" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <f.icon className="w-5 h-5 shrink-0" />
              {f.label}
            </div>
          ))}
        </div>
      </section>

      <HalloweenBanner />

      {/* SPLIT MERCHANDISING — clear entry points per fulfillment (Brief) */}
      <Reveal>
      <section className="max-w-[1400px] mx-auto px-6 md:px-8 pb-4 pt-8" data-testid="split-merchandising">
        <div className="grid sm:grid-cols-2 gap-4">
          <Link to="/shop?avail=instore" className="group bg-[#15803d] text-white p-6 rounded-lg flex items-center justify-between hover:brightness-110 transition-[filter]" data-testid="home-instore-entry">
            <div>
              <div className="font-display font-black text-2xl tracking-tight">Available in store now</div>
              <div className="text-white/80 text-sm mt-1">On the floor today — free same-day pickup or local delivery.</div>
            </div>
            <ArrowRight className="w-6 h-6 shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/shop?avail=ships" className="group bg-[#0A1C40] text-white p-6 rounded-lg flex items-center justify-between hover:brightness-125 transition-[filter]" data-testid="home-ships-entry">
            <div>
              <div className="font-display font-black text-2xl tracking-tight text-[#FFC220]">Ships to you</div>
              <div className="text-white/80 text-sm mt-1">Delivered by carrier straight to your door — hundreds of finds.</div>
            </div>
            <ArrowRight className="w-6 h-6 shrink-0 text-[#FFC220] group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
      </Reveal>

      {/* HOW DO YOU WANT YOUR ITEMS */}
      <Reveal>
      <section className="max-w-[1400px] mx-auto px-6 md:px-8 py-14" data-testid="fulfillment-options-section">
        <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
          How do you want your items?
        </h2>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {FULFILLMENT_CARDS.map((f) => (
            <Link
              key={f.title}
              to={f.link}
              className="group border border-gray-200 rounded-lg p-6 hover:border-[#0A1C40] hover:shadow-lg transition-[border-color,box-shadow] flex flex-col"
              data-testid={f.testid}
            >
              <div className="w-12 h-12 rounded-full bg-[#0A1C40]/5 flex items-center justify-center">
                <f.icon className="w-6 h-6 text-[#0A1C40]" />
              </div>
              <div className="font-display font-bold text-lg mt-4">{f.title}</div>
              <div className="text-sm text-gray-600 mt-1 leading-relaxed">{f.desc}</div>
              <div className="mt-4 text-sm font-bold text-[#0A1C40] group-hover:underline inline-flex items-center gap-1">
                {f.cta} <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>
      </Reveal>

      {/* DEALS UNDER $20 — vibrant, can't-miss section */}
      {under20.length > 0 && (
        <section className="bg-[#0A1C40] relative overflow-hidden" data-testid="home-deals-under-20-section">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-72 h-72 bg-[#FFC220] rounded-full blur-3xl" />
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#FFC220] rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-8 py-14 lg:py-16">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#FFC220] text-[#0A1C40] text-[10px] font-black tracking-[0.2em] uppercase px-3 py-1.5">
                  🔥 Brand-New Closeout
                </div>
                <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl tracking-tighter mt-3 text-white">
                  $1, $2, $5, $10 &amp; <span className="text-[#FFC220]">$20</span> finds.
                </h2>
                <p className="text-white/75 mt-3 max-w-xl">
                  Real overstock from our showroom — small things that still sting at full price elsewhere.
                </p>
              </div>
              <Link
                to="/deals-under-20"
                className="inline-flex items-center gap-2 bg-[#FFC220] text-[#0A1C40] font-black px-6 py-3 hover:bg-white transition-colors self-start lg:self-end"
                data-testid="home-deals-cta"
              >
                Shop all Deals Under $20 <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
              {under20.slice(0, 6).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QLIFE E-BIKE SPOTLIGHT */}
      <EbikeSpotlight />

      {/* DEPARTMENTS */}
      <Reveal>
      <section className="max-w-[1400px] mx-auto px-6 md:px-8 pb-14">
        <div className="flex items-end justify-between mb-6 gap-6 flex-wrap">
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
            <KineticText text="Shop by department" step={0.02} />
          </h2>
          <Link to="/shop" className="text-sm font-bold text-[#0A1C40] hover:underline inline-flex items-center gap-1" data-testid="all-categories-btn">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {CATEGORY_TILES.filter((c) => liveCats === null || liveCats.includes(c.name)).map((c) => (
            <Link
              key={c.name}
              to={`/shop/${c.name}`}
              className="group rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-[border-color,box-shadow]"
              data-testid={`category-tile-${c.name}`}
            >
              <div className="aspect-square overflow-hidden bg-gray-50 relative">
                <img
                  src={c.img}
                  alt={c.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                {c.accent && (
                  <span className="absolute top-2 left-2 bg-[#DC2626] text-white text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-sm">
                    Wholesale
                  </span>
                )}
              </div>
              <div className={`p-2.5 text-center text-xs md:text-sm font-bold leading-tight ${c.accent ? "text-[#DC2626]" : "text-gray-800 group-hover:text-[#0A1C40]"}`}>
                {c.name}
              </div>
            </Link>
          ))}
        </div>
      </section>
      </Reveal>

      {/* HOT DEALS */}
      {deals.length > 0 && (
        <section className="bg-gray-100 border-y border-gray-200" data-testid="deals-section">
          <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-14">
            <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-3 py-1 text-xs font-black tracking-[0.25em] uppercase rounded-sm">
                  <Flame className="w-3.5 h-3.5" /> Hot Deals
                </div>
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-3">
                  Save big. <span className="text-[#DC2626]">While it lasts.</span>
                </h2>
              </div>
              <Link to="/deals" className="btn-outline inline-flex items-center gap-2 !py-2.5 !px-6 text-sm" data-testid="deals-shop-all-btn">
                See all deals <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {deals.slice(0, 8).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PALLET PROGRAM — three ways to buy (Addendum A) */}
      <section className="bg-[#FFC220]" data-testid="pallet-promo-banner">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="ipv-eyebrow text-[#0A1C40]/60">Pallet Sales · Wholesale</div>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[#0A1C40] mt-2">
                Three ways to buy a pallet.
              </h2>
              <p className="text-[#0A1C40] font-bold mt-2 max-w-xl" data-testid="pallet-guarantee-line">
                “Nobody else in liquidation stands behind a pallet. We do.”
              </p>
            </div>
            <Link
              to="/pallets"
              className="shrink-0 rounded-full bg-[#0A1C40] text-white font-bold px-8 py-3.5 hover:bg-[#152b59] transition-colors inline-flex items-center gap-2 self-start md:self-end"
              data-testid="pallet-promo-cta"
            >
              Shop Pallets <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-6 grid md:grid-cols-3 gap-3 text-sm">
            <Link to="/pallets/manifested" className="bg-white/70 hover:bg-white transition-colors border-2 border-[#0A1C40] p-4" data-testid="home-pallet-manifested">
              <div className="font-black text-[#0A1C40]">MANIFESTED</div>
              <div className="text-[#0A1C40]/80 mt-1">Know what you're buying — pay the listed pallet price. Want one item swapped? $99 swap fee per order.</div>
            </Link>
            <Link to="/pallets/unmanifested" className="bg-white/70 hover:bg-white transition-colors border-2 border-[#0A1C40] p-4" data-testid="home-pallet-unmanifested">
              <div className="font-black text-[#0A1C40]">UNMANIFESTED</div>
              <div className="text-[#0A1C40]/80 mt-1">Lower cost, sold as-is with no manifest — buy it on the spot at the listed price.</div>
            </Link>
            <Link to="/build-pallet" className="bg-[#0A1C40] hover:bg-black transition-colors p-4" data-testid="home-pallet-custom">
              <div className="font-black text-[#FFC220]">CUSTOMIZE YOUR OWN</div>
              <div className="text-white/85 mt-1">3 items $500 · 5 items $900 · 7 items $1,500 · 11 items $2,200 — merchandise included, plus a flat $299 customization fee.</div>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <Reveal>
      <section className="max-w-[1400px] mx-auto px-6 md:px-8 py-14">
        <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
          <div>
            <div className="ipv-eyebrow text-[#0A1C40]">Just on the floor</div>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-2">
              <KineticText text="Featured & on sale" step={0.02} />
            </h2>
          </div>
          <Link to="/shop" className="btn-primary inline-flex items-center gap-2 !py-2.5 !px-6 text-sm" data-testid="shop-all-btn">
            Shop all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>
      </Reveal>

      {/* DELIVERY EXPLAINER */}
      <section className="bg-[#0A1C40] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-16 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="ipv-eyebrow text-[#FFC220]">Delivery & Pickup</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-4 leading-tight">
              $299+? Delivery&apos;s free.<br />
              <span className="text-white/60">Within 10 miles.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Enter your address at checkout — we&apos;ll calculate the exact delivery
              fee based on driving distance. Or skip delivery entirely and pick up
              from our Grand Rapids, MI showroom for free.
            </p>
            <Link
              to="/policies/delivery"
              className="mt-6 inline-flex items-center gap-2 text-[#FFC220] font-bold hover:text-white transition-colors text-sm"
              data-testid="home-delivery-policy-link"
            >
              See the full fee schedule →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/5 border border-white/10 rounded-lg p-7">
              <Truck className="w-7 h-7 text-[#FFC220]" />
              <div className="font-display font-black text-3xl mt-5">FREE</div>
              <div className="ipv-eyebrow text-white/40 mt-1">$299+ · 10mi</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-7">
              <Tag className="w-7 h-7 text-[#FFC220]" />
              <div className="font-display font-black text-3xl mt-5">$39</div>
              <div className="ipv-eyebrow text-white/40 mt-1">Under $299 · 10mi</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-7">
              <Store className="w-7 h-7 text-[#FFC220]" />
              <div className="font-display font-black text-3xl mt-5">Pickup</div>
              <div className="ipv-eyebrow text-white/40 mt-1">Always free</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-7">
              <ShieldCheck className="w-7 h-7 text-[#FFC220]" />
              <div className="font-display font-black text-3xl mt-5">Square</div>
              <div className="ipv-eyebrow text-white/40 mt-1">Secure pay</div>
            </div>
          </div>
        </div>
      </section>

      {/* AUTO-ROTATING TESTIMONIALS + STORE TRUST CARD */}
      <TestimonialSlider />

      <FaqSection />
    </div>
  );
}
